import React, { useEffect, useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Users, Search, Music, Loader2, Clock } from 'lucide-react';
import { ref as dbRef, onValue, off } from 'firebase/database';

export default function UsersManager({ db }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    const usersRef = dbRef(db, 'users');

    onValue(
      usersRef,
      (snapshot) => {
        const data = snapshot.val();
        const list = data
          ? Object.entries(data).map(([uid, value]) => ({ uid, ...value }))
          : [];
        setUsers(list);
        setLoading(false);
      },
      (err) => {
        console.error('Error fetching users:', err);
        setLoading(false);
      }
    );

    return () => off(usersRef);
  }, [db]);

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return users;
    return users.filter((u) =>
      [u.displayName, u.email, u.uid].some((v) =>
        (v || '').toLowerCase().includes(q)
      )
    );
  }, [users, search]);

  const totalPurchases = useMemo(
    () => users.reduce((sum, u) => sum + Object.keys(u.purchases || {}).length, 0),
    [users]
  );

  const newThisMonth = useMemo(() => {
    const now = new Date();
    return users.filter((u) => {
      if (!u.createdAt) return false;
      const d = new Date(u.createdAt);
      return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
    }).length;
  }, [users]);

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getInitial = (u) => {
    if (u.displayName) return u.displayName.charAt(0).toUpperCase();
    if (u.email) return u.email.charAt(0).toUpperCase();
    return '?';
  };

  const purchaseCount = (u) => Object.keys(u.purchases || {}).length;

  const StatCard = ({ icon: Icon, label, value }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#243137] p-4 sm:p-6 rounded-xl shadow-lg text-white flex items-center gap-3 sm:gap-4 border border-[#bd9f67]/20 hover:border-[#bd9f67]/40 hover:shadow-[0_0_25px_rgba(189,159,103,0.18)] transition"
    >
      <div className="bg-[#bd9f67]/15 p-2 sm:p-4 rounded-lg flex-shrink-0">
        <Icon size={24} className="sm:w-8 sm:h-8 text-[#bd9f67]" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs sm:text-sm opacity-90 truncate">{label}</p>
        <p className="text-xl sm:text-3xl font-bold truncate">{value}</p>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl md:text-3xl font-bold text-[#f9f9f9] mb-2 tracking-tight">
          App Users
        </h2>
        <p className="text-sm text-[#a6a6b5]">
          Everyone who has registered an account in the app.
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 sm:gap-4">
        <StatCard icon={Users} label="Total Users" value={users.length} />
        <StatCard icon={Music} label="Total Purchases" value={totalPurchases} />
        <StatCard icon={Clock} label="New This Month" value={newThisMonth} />
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#7d7d8a]" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or email..."
          className="w-full pl-10 pr-4 py-2.5 bg-[#16161b] border border-[#2a2a33] rounded-lg text-[#c9c9d4] focus:outline-none focus:ring-2 focus:ring-[#bd9f67] placeholder-[#7d7d8a]"
        />
      </div>

      {/* Loading */}
      {loading && (
        <div className="flex items-center justify-center py-16 text-[#7d7d8a]">
          <Loader2 size={24} className="animate-spin mr-2" />
          Loading users...
        </div>
      )}

      {/* Users Table */}
      {!loading && filteredUsers.length > 0 && (
        <div className="bg-[#16161b] shadow rounded-lg overflow-hidden border border-[#bd9f67]/15">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#243137] border-b border-[#2a2a33]">
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67]">User</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67]">Email</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67] hidden md:table-cell">Provider</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67] hidden lg:table-cell">Joined</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67] hidden lg:table-cell">Last Login</th>
                  <th className="px-3 sm:px-6 py-3 text-left text-xs font-semibold text-[#bd9f67]">Purchases</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#2a2a33]">
                {filteredUsers.map((u) => (
                  <tr key={u.uid} className="hover:bg-[#1e1e25] transition duration-150">
                    <td className="px-3 sm:px-6 py-3">
                      <div className="flex items-center gap-3">
                        {u.photoURL ? (
                          <img
                            src={u.photoURL}
                            alt={u.displayName || u.email}
                            className="w-8 h-8 rounded-full object-cover flex-shrink-0 border border-[#bd9f67]/30"
                          />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#bd9f67]/20 text-[#bd9f67] flex items-center justify-center font-semibold flex-shrink-0">
                            {getInitial(u)}
                          </div>
                        )}
                        <span className="font-medium text-[#f9f9f9] truncate max-w-[140px]">
                          {u.displayName || 'Anonymous'}
                        </span>
                      </div>
                    </td>
                    <td className="px-3 sm:px-6 py-3 text-[#c9c9d4] break-all">{u.email || '—'}</td>
                    <td className="px-3 sm:px-6 py-3 text-[#a6a6b5] hidden md:table-cell capitalize">
                      {u.provider ? String(u.provider).replace('.com', '') : '—'}
                    </td>
                    <td className="px-3 sm:px-6 py-3 text-[#a6a6b5] hidden lg:table-cell whitespace-nowrap">
                      {formatDate(u.createdAt)}
                    </td>
                    <td className="px-3 sm:px-6 py-3 text-[#a6a6b5] hidden lg:table-cell whitespace-nowrap">
                      {formatDate(u.lastLoginAt)}
                    </td>
                    <td className="px-3 sm:px-6 py-3">
                      <span className="px-2 py-1 rounded-full text-xs font-semibold bg-[#bd9f67]/15 text-[#bd9f67] whitespace-nowrap">
                        {purchaseCount(u)}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Empty State */}
      {!loading && filteredUsers.length === 0 && (
        <div className="bg-[#16161b] shadow rounded-lg p-12 text-center border border-[#bd9f67]/15">
          <Users size={40} className="mx-auto mb-3 text-[#7d7d8a]" />
          <p className="text-[#7d7d8a]">
            {users.length === 0
              ? 'No users registered yet.'
              : 'No users match your search.'}
          </p>
        </div>
      )}
    </div>
  );
}
